import React, { useContext, useMemo } from 'react';
import { get, isEmpty, startCase, toLower } from 'lodash';
import classNames from 'classnames';

import { GetDirectionsButton } from 'app/about/components/About/components/index';
import { CustomerContext, TenantContext } from 'app/common/contexts';
import { useFormatMessage, useNationalSiteContext } from 'app/common/hooks';
import { normalizePhoneNumberWithDashes } from 'app/common/utils/AddressUtils';

import messages from './DealerInfo.messages';
import { AuroraPayImageWhite } from 'app/checkout/utils/PaymentMethodUtil.js';
import { Environment } from 'app/common/services/index.js';

const DealerInfo = ({ application }) => {
  const formatMessage = useFormatMessage();
  const phoneNumber = get(application, 'phoneNumber');
  const { nationalSite, dealerNetwork, applicationThemeSource } =
    useNationalSiteContext();
  const { tenant } = React.useContext(TenantContext);

  // Fallback to tenant logo if no application logo is available
  const { contentUrl, altText, title } =
    get(application, 'logoAsset') ||
    get(applicationThemeSource, 'logoAsset') ||
    get(tenant, 'logoAsset', {});

  const displayName = getAppDisplayName(application, dealerNetwork);

  const { customer } = useContext(CustomerContext);

  const isLocationFleetPerformEnabled = useMemo(() => {
    return (
      !!customer &&
      get(customer, 'attributes.fleetPerformEnabled', false) &&
      auroraPayEnabled() &&
      get(application, 'fleetPerformEnabled', false)
    );
  }, [customer, application]);

  return (
    <div className="flex flex-col justify-between h-full w-full">
      <img
        className="m-5 h-24 object-scale-down"
        alt={altText || formatMessage(messages.logoAlt)}
        title={title}
        src={contentUrl}
      />
      <div
        className={classNames('text-white flex-grow', {
          'bg-customGray': !!nationalSite,
          'bg-primary-500': !nationalSite
        })}
      >
        <div className="flex flex-row justify-between">
          <h4 className="mt-3 mb-3 ml-4 text-lg font-bold">{displayName}</h4>
          <GetDirectionsButton
            className="p-3 m-2 flex lg:hidden"
            application={application}
          />
        </div>
        <div className="flex flex-row justify-between">
          <div>
            <DisplayAddress
              line1={application.line1}
              city={application.city}
              state={application.state}
              zip={application.zip}
            />
            {!isEmpty(phoneNumber) && (
              <p className="ml-4 mt-1 mb-3">
                {formatMessage(messages.callUs)}{' '}
                <a
                  href={'tel:' + phoneNumber}
                  className="inline-flex text-primary-100 hover:text-primary-200"
                >
                  {normalizePhoneNumberWithDashes(phoneNumber)}
                </a>
              </p>
            )}
          </div>
          <div
            className={
              'about-dealer-info px-4 lg:px-0 lg:mr-2 lg:mt-6 xl:px-2 xl:mr-0 xl:mt-0'
            }
          >
            {isLocationFleetPerformEnabled && (
              <AuroraPayImageWhite className={'h-5'} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Capitalizes the first letter of every word in a string without allowing the string to be in all caps.
 *
 * @param string to capitalize
 * @returns {*} modified string with capitalizations
 */
function titleCase(string) {
  return startCase(toLower(string));
}

/**
 * Puts address information into an address format of
 *
 * STREET
 * CITY, STATE ZIP
 *
 * @param className Parent classname information
 * @param line1 Street information of the address
 * @param city City of the address
 * @param state State of the address
 * @param zip Zipcode of the address
 * @returns {JSX.Element}
 * @constructor
 */
const DisplayAddress = ({ className, line1, city, state, zip }) => {
  return (
    <p
      className={classNames('ml-4', {
        [className]: !!className
      })}
    >
      {titleCase(line1)} <br /> {titleCase(city) + ', ' + state + ' ' + zip}
    </p>
  );
};

function getAppDisplayName(application, dealerNetwork) {
  const displayName = dealerNetwork
    ? get(application, 'displayName')
    : get(application, 'name');
  if (displayName === get(application, 'name')) {
    return titleCase(displayName);
  }
  return displayName;
}

function auroraPayEnabled() {
  return Environment.get('AURORA_PAY_ENABLED', 'false') === 'true';
}

export default DealerInfo;
export { DealerInfo };
